/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { Button } from 'reactstrap';
import { Loader } from '@g17eco/atoms/loader';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { DataQualityIssueType } from '@api/data-quality';
import { DataQualityResultsTable, GroupedIssue } from './DataQualityResultsTable';
import { ResultsFilterState } from './constants';

interface SelectOption {
  value: string;
  label: string;
  searchString: string;
}

interface ResultsFilterOptions {
  initiativeNames: SelectOption[];
  issueTypes: SelectOption[];
  statuses: SelectOption[];
}

interface DataQualityResultsSectionProps {
  isLoading: boolean;
  hasJob: boolean;
  hasResults: boolean;
  totalResultsCount: number;
  filteredResultsCount: number;
  lastRunDate?: string;
  lastRunDateFull?: string;
  lastRunFilterSummary?: string;
  downloading: boolean;
  resultsFilters: ResultsFilterState;
  resultsFilterOptions: ResultsFilterOptions;
  groupedResults: GroupedIssue[];
  expandedGroups: Set<string>;
  hasActiveResultsFilters: boolean;
  onDownloadCsv: () => void;
  onResultsFilterChange: (key: keyof ResultsFilterState, values: string[] | DataQualityIssueType[]) => void;
  onClearResultsFilters: () => void;
  onToggleGroup: (utrvId: string) => void;
  onToggleAllGroups: () => void;
}

export const DataQualityResultsSection = ({
  isLoading,
  hasJob,
  hasResults,
  totalResultsCount,
  filteredResultsCount,
  lastRunDate,
  lastRunDateFull,
  lastRunFilterSummary,
  downloading,
  resultsFilters,
  resultsFilterOptions,
  groupedResults,
  expandedGroups,
  hasActiveResultsFilters,
  onDownloadCsv,
  onResultsFilterChange,
  onClearResultsFilters,
  onToggleGroup,
  onToggleAllGroups,
}: DataQualityResultsSectionProps) => {
  return (
    <div className='mt-4 pt-3 border-top'>
      {/* Header with last run info */}
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <div>
          {lastRunDate && (
            <span className='text-muted' title={lastRunDateFull}>
              <i className='fas fa-clock me-1' />
              Last run: {lastRunDate}
              {lastRunFilterSummary && (
                <span className='ms-2'>• {lastRunFilterSummary}</span>
              )}
            </span>
          )}
          {hasResults && (
            <span className={`ms-3 ${totalResultsCount > 0 ? 'text-warning' : 'text-success'}`}>
              <strong>{totalResultsCount}</strong> issue{totalResultsCount !== 1 ? 's' : ''} found
              {hasActiveResultsFilters && filteredResultsCount !== totalResultsCount && (
                <span className='text-muted'> ({filteredResultsCount} shown)</span>
              )}
            </span>
          )}
        </div>
        {totalResultsCount > 0 && hasJob && (
          <Button color='secondary' onClick={onDownloadCsv} disabled={isLoading || downloading}>
            <i className='fas fa-download me-2' />
            {downloading ? 'Downloading...' : 'Download CSV'}
          </Button>
        )}
      </div>

      {/* Results filters */}
      {totalResultsCount > 0 && (
        <div className='d-flex gap-2 mb-3 align-items-end'>
          <SelectFactory
            selectType={SelectTypes.MultipleSelect}
            placeholder='Reporting Level'
            className='flex-grow-1'
            onChange={(values) => onResultsFilterChange('initiativeNames', values)}
            options={resultsFilterOptions.initiativeNames}
            values={resultsFilters.initiativeNames}
            isMenuPortalTargetBody
            isFullWidthOptions
          />
          <SelectFactory
            selectType={SelectTypes.MultipleSelect}
            placeholder='Issue Type'
            className='flex-grow-1'
            onChange={(values) => onResultsFilterChange('issueTypes', values as DataQualityIssueType[])}
            options={resultsFilterOptions.issueTypes}
            values={resultsFilters.issueTypes}
            isMenuPortalTargetBody
          />
          <SelectFactory
            selectType={SelectTypes.MultipleSelect}
            placeholder='Status'
            className='flex-grow-1'
            onChange={(values) => onResultsFilterChange('statuses', values)}
            options={resultsFilterOptions.statuses}
            values={resultsFilters.statuses}
            isMenuPortalTargetBody
          />
          {hasActiveResultsFilters && (
            <Button color='link' className='text-muted p-0' onClick={onClearResultsFilters}>
              Clear
            </Button>
          )}
        </div>
      )}

      {/* Results content */}
      {isLoading ? (
        <Loader />
      ) : !hasJob ? (
        <div className='text-center py-5'>
          <i className='fas fa-clipboard-check text-muted fa-3x mb-3' />
          <p className='text-muted'>No data quality check has been run yet.</p>
          <p className='text-muted'>Select the checks to run and click "Run Checks" to analyze your data.</p>
        </div>
      ) : hasResults ? (
        <>
          {totalResultsCount === 0 ? (
            <div className='text-center py-5'>
              <i className='fas fa-check-circle text-success fa-3x mb-3' />
              <p className='text-muted'>No issues found.</p>
            </div>
          ) : filteredResultsCount === 0 ? (
            <div className='text-center py-5'>
              <i className='fas fa-filter text-muted fa-3x mb-3' />
              <p className='text-muted'>No issues match the current filters.</p>
              <Button color='link' onClick={onClearResultsFilters}>
                Clear filters
              </Button>
            </div>
          ) : (
            <DataQualityResultsTable
              groupedResults={groupedResults}
              expandedGroups={expandedGroups}
              onToggleGroup={onToggleGroup}
              onToggleAll={onToggleAllGroups}
            />
          )}
        </>
      ) : null}
    </div>
  );
};
